import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
  {
    quote: "PolicyAlerts has transformed how our practice manages coverage changes. The real-time alerts ensure we never miss Medicare or commercial payer updates that affect our patients. It\'s become an essential tool for our entire team.",
    author: "Dr. Sarah Mitchell",
    role: "Medical Director",
    company: "Midwest ENT Specialists",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d543082-1763294235942.png",
    imageAlt: "Professional headshot of Dr. Sarah Mitchell, Caucasian female physician with blonde hair in white medical coat against neutral background",
    rating: 5
  },
  {
    quote: "The depth of policy analysis and coverage tracking from PolicyAlerts gives us a competitive advantage. We\'re able to anticipate policy changes and adjust our market access strategies proactively. The ROI has been exceptional.",
    author: "James Chen",
    role: "VP of Market Access",
    company: "Advanced Medical Devices Inc.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_191e34b29-1763293592318.png",
    imageAlt: "Professional headshot of James Chen, Asian male executive with short black hair in charcoal suit and blue tie",
    rating: 5
  },
  {
    quote: "After years with PolicyAlerts, I can confidently say it's the most reliable policy monitoring platform available. The comprehensive coverage across all payers and the analytics dashboard have been instrumental in our strategic planning.",
    author: "Dr. Michael Rodriguez",
    role: "Chief Medical Officer",
    company: "Specialty Care Partners",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f147df5d-1763301649610.png",
    imageAlt: "Professional headshot of Dr. Michael Rodriguez, Hispanic male physician with dark hair in white medical coat against corporate background",
    rating: 5
  }];


  return (
    <section className="bg-white py-16 md:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#10B981]/10 rounded-full mb-4 md:mb-6">
            <Icon name="Star" size={16} color="#10B981" />
            <span className="text-xs md:text-sm font-medium text-[#10B981]">Client Success Stories</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B20] mb-4 md:mb-6">
            Trusted by Healthcare Leaders
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            See why provider groups, specialty practices, and healthcare organizations choose PolicyAlerts for their policy monitoring needs.
          </p>
        </div>

        <div className="relative">
          <div className="bg-gradient-to-br from-[#1A73E8]/5 to-[#ED1C24]/5 rounded-2xl p-8 md:p-12 lg:p-16">
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-center mb-6 md:mb-8">
                {[...Array(testimonials?.[activeTestimonial]?.rating)]?.map((_, idx) =>
                <Icon key={idx} name="Star" size={24} color="#F59E0B" className="fill-[#F59E0B]" />
                )}
              </div>

              <blockquote className="text-lg md:text-xl lg:text-2xl text-[#1B1B20] text-center mb-8 md:mb-10 leading-relaxed">
                "{testimonials?.[activeTestimonial]?.quote}"
              </blockquote>

              <div className="flex flex-col items-center gap-4 md:gap-6">
                <Image
                  src={testimonials?.[activeTestimonial]?.image}
                  alt={testimonials?.[activeTestimonial]?.imageAlt}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-4 border-white shadow-lg" />

                <div className="text-center">
                  <p className="text-base md:text-lg font-bold text-[#1B1B20]">
                    {testimonials?.[activeTestimonial]?.author}
                  </p>
                  <p className="text-sm md:text-base text-gray-600">
                    {testimonials?.[activeTestimonial]?.role}
                  </p>
                  <p className="text-sm md:text-base text-[#1A73E8] font-medium">
                    {testimonials?.[activeTestimonial]?.company}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-2 md:gap-3 mt-6 md:mt-8">
            {testimonials?.map((_, idx) =>
            <button
              key={idx}
              onClick={() => setActiveTestimonial(idx)}
              className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all ${
              activeTestimonial === idx ?
              'bg-[#1A73E8] w-8 md:w-12' :
              'bg-gray-300 hover:bg-gray-400'}`
              }
              aria-label={`View testimonial ${idx + 1}`} />

            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-16">
          <div className="group p-6 md:p-8 bg-gradient-to-br from-[#1A73E8]/5 to-[#1A73E8]/10 rounded-xl border border-[#1A73E8]/20 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-4">
              <Icon name="DollarSign" size={40} className="text-[#1A73E8]" color="#1A73E8" />
              <div className="flex items-center gap-1 bg-[#10B981]/10 px-2 py-1 rounded-full">
                <Icon name="TrendingUp" size={12} color="#10B981" />
                <span className="text-xs font-semibold text-[#10B981]">+340%</span>
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-[#1B1B20] mb-2">$2.4M Average ROI</p>
            <p className="text-sm md:text-base text-gray-600 mb-3">Annual cost savings from prevented claim denials and optimized coverage strategies</p>
            <div className="pt-3 border-t border-[#1A73E8]/20">
              <p className="text-xs text-gray-500">Based on enterprise client data (500+ employees)</p>
            </div>
          </div>
          <div className="group p-6 md:p-8 bg-gradient-to-br from-[#10B981]/5 to-[#10B981]/10 rounded-xl border border-[#10B981]/20 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-4">
              <Icon name="Clock" size={40} className="text-[#10B981]" color="#10B981" />
              <div className="flex items-center gap-1 bg-[#ED1C24]/10 px-2 py-1 rounded-full">
                <Icon name="Zap" size={12} color="#ED1C24" />
                <span className="text-xs font-semibold text-[#ED1C24]">85% Faster</span>
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-[#1B1B20] mb-2">18 Hours Saved Weekly</p>
            <p className="text-sm md:text-base text-gray-600 mb-3">Time eliminated from manual policy monitoring and research per compliance team</p>
            <div className="pt-3 border-t border-[#10B981]/20">
              <p className="text-xs text-gray-500">Average across 500+ healthcare organizations</p>
            </div>
          </div>
          <div className="group p-6 md:p-8 bg-gradient-to-br from-[#ED1C24]/5 to-[#ED1C24]/10 rounded-xl border border-[#ED1C24]/20 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-4">
              <Icon name="Shield" size={40} className="text-[#ED1C24]" color="#ED1C24" />
              <div className="flex items-center gap-1 bg-[#1A73E8]/10 px-2 py-1 rounded-full">
                <Icon name="CheckCircle" size={12} color="#1A73E8" />
                <span className="text-xs font-semibold text-[#1A73E8]">99.7%</span>
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-[#1B1B20] mb-2">Zero Missed Changes</p>
            <p className="text-sm md:text-base text-gray-600 mb-3">Critical policy updates caught before impacting patient care or revenue cycle</p>
            <div className="pt-3 border-t border-[#ED1C24]/20">
              <p className="text-xs text-gray-500">Verified across 100K+ daily policy documents</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;